/**
 * NPC acts (กิจวัตร): what a townsperson does with their hands from moment to moment, picked by the
 * situation. Each role has a table of acts with weights and conditions (the time of day from
 * data/time.ts, the weather from data/weather.ts, the place, the work at the station); reactions to
 * the player and to a fight come first (a greeting, cowering from a red name, fleeing a fight,
 * cheering a bout at the arena). `systems/npcActs.ts` does the picking through the World's `Rng` and
 * writes the act on the entity (`e.act`, `e.actUntil`); the renderer plays the act's `motion`.
 *
 * Data only. Add an act here (and its motion in apps/client/src/render3d/motions/groups/npcActs.ts),
 * then give it to the roles that do it.
 */
import type { Activity, DayPart } from './folk';
import type { WeatherKind } from './weather';
import { START, type ZoneId } from './zones';

export type NpcRole =
  | 'villager'
  | 'vendor'
  | 'guard'
  | 'monk'
  | 'child'
  | 'elder'
  | 'fisher'
  | 'smith'
  | 'scholar'
  | 'performer'
  | 'disciple'
  | 'farmer'
  | 'innkeeper';

/** What kind of place a spot is (an act may want one). */
export type PlaceTag = 'plaza' | 'market' | 'inn' | 'water' | 'field' | 'yard' | 'shrine' | 'garden' | 'street' | 'arena';

export interface NpcActDef {
  /** Thai, for tools and tooltips. */
  readonly name: string;
  /** The 3D motion played standing (render3d/motions). */
  readonly motion: string;
  /** The upper-body motion played while walking, if the act carries on in stride (an umbrella, a lantern). */
  readonly walk?: string;
  /** Seconds held (a random span). */
  readonly dur: readonly [number, number];
  /** Done with a partner of the same role close by (a chat, a game, a bout): both play it, facing. */
  readonly pair?: boolean;
  /** The body moves: running from danger, chasing round (tag), walking the beat, a step out of the way. */
  readonly move?: 'flee' | 'circle' | 'patrol' | 'aside';
}


/** Every act, by id. The dotted ids are the daily events' (stalls, the lion dance, the opera, the festival). */
const ACTS = {
  // ── the hours of the day ──
  taichi: { name: 'รำมวยไท่เก๊กยามเช้า', motion: 'npcTaichi', dur: [14, 26] },
  stretch: { name: 'บิดขี้เกียจ', motion: 'npcStretch', dur: [3, 5] },
  yawn: { name: 'หาว', motion: 'npcYawn', dur: [3, 4] },
  eat: { name: 'กินข้าวจากชาม', motion: 'npcEat', dur: [10, 18] },
  pourTea: { name: 'รินชา', motion: 'npcPourTea', dur: [4, 6] },
  drinkTea: { name: 'จิบชา', motion: 'npcDrinkTea', dur: [6, 12] },
  skyLook: { name: 'มองฟ้า', motion: 'npcSkyLook', dur: [4, 7] },
  lightLantern: { name: 'จุดโคม', motion: 'npcLightLantern', dur: [5, 7] },
  patrol: { name: 'ถือโคมเดินตรวจ', motion: 'npcLanternHold', walk: 'npcLanternHold', dur: [20, 40], move: 'patrol' },
  sleepBench: { name: 'นั่งหลับ', motion: 'npcSleep', dur: [20, 40] },
  drunk: { name: 'เมาเดินโซเซ', motion: 'npcDrunk', dur: [10, 20] },
  // ── the weather ──
  umbrella: { name: 'กางร่ม', motion: 'npcUmbrella', walk: 'npcUmbrella', dur: [15, 30] },
  shelter: { name: 'เอามือบังฝน', motion: 'npcShelter', walk: 'npcShelter', dur: [6, 12] },
  shiver: { name: 'หนาวสั่น', motion: 'npcShiver', dur: [6, 12] },
  fan: { name: 'พัดคลายร้อน', motion: 'npcFan', dur: [6, 12] },
  wipeSweat: { name: 'ปาดเหงื่อ', motion: 'npcWipeSweat', dur: [3, 4] },
  // ── among people ──
  chat: { name: 'ซุบซิบ', motion: 'npcChat', dur: [10, 20], pair: true },
  argue: { name: 'ทะเลาะ', motion: 'npcArgue', dur: [6, 12], pair: true },
  laugh: { name: 'หัวเราะ', motion: 'npcLaugh', dur: [3, 5] },
  sigh: { name: 'ถอนหายใจ', motion: 'npcSigh', dur: [3, 4] },
  scratchHead: { name: 'เกาหัว', motion: 'npcScratchHead', dur: [3, 4] },
  // ── to the player ──
  baoquan: { name: 'ประสานมือคารวะ', motion: 'npcBaoquan', dur: [1.4, 1.6] },
  bow: { name: 'ค้อมคำนับ', motion: 'npcBow', dur: [1.4, 1.6] },
  wave: { name: 'โบกมือ', motion: 'npcWave', dur: [1.4, 1.8] },
  stepAside: { name: 'หลบทาง', motion: 'npcStepAside', dur: [1.6, 2], move: 'aside' },
  cower: { name: 'หดตัวกลัว', motion: 'npcCower', dur: [2.5, 4] },
  cheer: { name: 'โห่เชียร์', motion: 'npcCheer', dur: [3, 6] },
  flee: { name: 'วิ่งหนี', motion: 'npcCower', dur: [2.5, 3.5], move: 'flee' },
  // ── trade ──
  beckon: { name: 'กวักมือเรียกลูกค้า', motion: 'npcBeckon', dur: [4, 8] },
  haggle: { name: 'ต่อราคา', motion: 'npcHaggle', dur: [5, 10] },
  countCoins: { name: 'นับเหรียญ', motion: 'npcCountCoins', dur: [5, 9] },
  // ── work ──
  carryBuckets: { name: 'หาบน้ำ', motion: 'npcBucketsRest', walk: 'npcBuckets', dur: [10, 20], move: 'patrol' },
  chopWood: { name: 'ผ่าฟืน', motion: 'npcChopWood', dur: [12, 24] },
  hammer: { name: 'ตีเหล็ก', motion: 'npcHammer', dur: [12, 24] },
  washClothes: { name: 'ซักผ้า', motion: 'npcWash', dur: [14, 26] },
  feedChickens: { name: 'โปรยข้าวให้ไก่', motion: 'npcFeedChickens', dur: [8, 14] },
  plantRice: { name: 'ดำนา', motion: 'npcPlantRice', dur: [14, 26] },
  row: { name: 'พายเรือ', motion: 'npcRow', dur: [14, 26] },
  sweep: { name: 'กวาดลาน', motion: 'sweep', dur: [10, 20] },
  // ── the arts ──
  guqin: { name: 'ดีดกู่ฉิน', motion: 'npcGuqin', dur: [16, 30] },
  flute: { name: 'เป่าขลุ่ย', motion: 'npcFlute', dur: [12, 24] },
  readScroll: { name: 'อ่านคัมภีร์', motion: 'npcReadScroll', dur: [10, 20] },
  calligraphy: { name: 'เขียนพู่กัน', motion: 'npcCalligraphy', dur: [12, 24] },
  xiangqi: { name: 'เดินหมากรุก', motion: 'npcXiangqi', dur: [16, 30], pair: true },
  dice: { name: 'ทอยลูกเต๋า', motion: 'npcDice', dur: [8, 16], pair: true },
  storyteller: { name: 'เล่านิทาน', motion: 'npcStoryteller', dur: [16, 30] },
  // ── children ──
  tag: { name: 'วิ่งไล่จับ', motion: 'run', dur: [8, 14], move: 'circle' },
  shuttlecock: { name: 'เตะลูกขนไก่', motion: 'npcShuttlecock', dur: [8, 14] },
  // ── the martial ──
  spar: { name: 'ประมือซ้อม', motion: 'npcSpar', dur: [12, 20], pair: true },
  meditate: { name: 'นั่งสมาธิ', motion: 'npcMeditate', dur: [16, 30] },
  // ── the daily events (stalls, festival, opera) ──
  'stall.setup': { name: 'จัดแผงสินค้า', motion: 'npcStallSetup', dur: [6, 10] },
  hawk: { name: 'ร้องขายของ', motion: 'npcHawk', dur: [4, 8] },
  'lion.head': { name: 'เชิดหัวสิงโต', motion: 'npcLionHead', dur: [10, 20] },
  'lion.tail': { name: 'เชิดหางสิงโต', motion: 'npcLionTail', dur: [10, 20] },
  drum: { name: 'ตีกลอง', motion: 'npcDrum', dur: [10, 20] },
  gong: { name: 'ตีฆ้อง', motion: 'npcGong', dur: [10, 20] },
  'opera.sing': { name: 'งิ้วร้อง', motion: 'npcOperaSing', dur: [10, 20] },
  'opera.fight': { name: 'งิ้วบู๊', motion: 'npcOperaFight', dur: [10, 20] },
  'watch.cheer': { name: 'โห่ร้องชม', motion: 'npcCheer', dur: [3, 6] },
  'watch.clap': { name: 'ปรบมือ', motion: 'npcClap', dur: [3, 6] },
  'lantern.carry': { name: 'ชูโคมเดิน', motion: 'npcLanternHigh', walk: 'npcLanternHigh', dur: [10, 30] },
  incense: { name: 'ไหว้ธูป', motion: 'npcIncense', dur: [6, 10] },
  'demo.form': { name: 'รำเพลงยุทธ์', motion: 'npcDemoForm', dur: [10, 20] },
} satisfies Record<string, NpcActDef>;

export type NpcActId = keyof typeof ACTS;
export const NPC_ACTS: Readonly<Record<NpcActId, NpcActDef>> = ACTS;

export const isNpcAct = (v: unknown): v is NpcActId => typeof v === 'string' && Object.hasOwn(NPC_ACTS, v);

/** When an act fits. Every field left out matches anything. */
export interface ActWhen {
  /** The named times of day (data/time.ts `TIME_PERIODS` ids). */
  readonly periods?: readonly string[];
  readonly parts?: readonly DayPart[];
  readonly weather?: readonly WeatherKind[];
  readonly notWeather?: readonly WeatherKind[];
  readonly places?: readonly PlaceTag[];
}

export interface ActChoice {
  readonly act: NpcActId;
  readonly w: number;
  readonly when?: ActWhen;
  /**
   * The station's work it may come in at (data/folk.ts `Activity`) for townsfolk on a schedule. Left out: only
   * when they are not at work (`FREE_WORK`).
   */
  readonly on?: readonly Activity[];
}

/** A townsperson's station activities during which the act table plays. */
export const FREE_WORK: readonly Activity[] = ['idle', 'sit', 'drink', 'call', 'stroll'];
/** How strongly a townsperson keeps on with the station's own work instead (a weight in the table). */
export const WORK_WEIGHT = 6;

const WET: readonly WeatherKind[] = ['rain', 'storm'];
const HOT = { periods: ['late', 'noon', 'afternoon'], weather: ['clear'] } as const satisfies ActWhen;
const DAWN = { periods: ['predawn', 'daybreak', 'early'] } as const satisfies ActWhen;
const NIGHT = { parts: ['dusk', 'night'] } as const satisfies ActWhen;
const DUSK = { periods: ['dusk', 'nightfall'] } as const satisfies ActWhen;
const MEAL = { periods: ['early', 'noon', 'dusk'] } as const satisfies ActWhen;

/** Shared by everyone: the weather and the small things people do. */
const COMMON: readonly ActChoice[] = [
  { act: 'umbrella', w: 14, when: { weather: WET } },
  { act: 'shelter', w: 5, when: { weather: WET } },
  { act: 'shiver', w: 10, when: { weather: ['snow'] } },
  { act: 'fan', w: 3, when: HOT },
  { act: 'wipeSweat', w: 2, when: HOT },
  { act: 'stretch', w: 2, when: DAWN },
  { act: 'yawn', w: 2, when: { parts: ['dawn', 'night'] } },
  { act: 'eat', w: 2, when: { ...MEAL, notWeather: WET } },
  { act: 'skyLook', w: 1 },
  { act: 'sigh', w: 1 },
  { act: 'scratchHead', w: 1 },
  { act: 'laugh', w: 1 },
];

const dry = (c: ActChoice): ActChoice => ({ ...c, when: { notWeather: [...WET, 'snow'], ...c.when } });

/** Each role's acts. */
export const ROLE_ACTS: Readonly<Record<NpcRole, readonly ActChoice[]>> = {
  villager: [
    ...COMMON,
    dry({ act: 'chat', w: 4 }),
    dry({ act: 'argue', w: 1 }),
    dry({ act: 'washClothes', w: 4, when: { places: ['water'], parts: ['dawn', 'day'] } }),
    dry({ act: 'feedChickens', w: 3, when: { parts: ['dawn', 'day'] } }),
    dry({ act: 'carryBuckets', w: 2, when: { parts: ['dawn', 'day'] } }),
    dry({ act: 'taichi', w: 3, when: DAWN }),
    { act: 'drunk', w: 4, when: { parts: ['night'], places: ['inn', 'street', 'plaza'] } },
    { act: 'sleepBench', w: 2, when: { periods: ['noon', 'afternoon', 'night', 'midnight'] } },
    dry({ act: 'dice', w: 2, when: { parts: ['day', 'dusk', 'night'] } }),
    { act: 'drinkTea', w: 2 },
  ],
  vendor: [
    ...COMMON,
    { act: 'beckon', w: 8, when: { parts: ['day', 'dusk'] } },
    { act: 'hawk', w: 6, when: { parts: ['day', 'dusk'] } },
    { act: 'haggle', w: 4, when: { parts: ['day'] } },
    { act: 'countCoins', w: 4 },
    { act: 'stall.setup', w: 4, when: DAWN },
    { act: 'pourTea', w: 2 },
    { act: 'sleepBench', w: 3, when: { periods: ['afternoon', 'night', 'midnight', 'deep'] } },
  ],
  innkeeper: [
    ...COMMON,
    { act: 'pourTea', w: 6 },
    { act: 'beckon', w: 5, when: { parts: ['day', 'dusk'] } },
    { act: 'countCoins', w: 4 },
    { act: 'sweep', w: 3, when: DAWN },
    { act: 'lightLantern', w: 6, when: DUSK },
  ],
  guard: [
    { act: 'umbrella', w: 6, when: { weather: WET } },
    { act: 'shiver', w: 4, when: { weather: ['snow'] } },
    { act: 'patrol', w: 10, when: NIGHT },
    { act: 'lightLantern', w: 6, when: DUSK },
    { act: 'yawn', w: 3, when: { parts: ['night', 'dawn'] } },
    { act: 'wipeSweat', w: 2, when: HOT },
    { act: 'stretch', w: 2, when: DAWN },
    { act: 'scratchHead', w: 1 },
    { act: 'skyLook', w: 2 },
    dry({ act: 'chat', w: 2 }),
    { act: 'sleepBench', w: 1, when: { periods: ['deep', 'midnight'] } },
  ],
  monk: [
    { act: 'umbrella', w: 6, when: { weather: WET } },
    { act: 'meditate', w: 8 },
    { act: 'sweep', w: 6, when: { parts: ['dawn', 'day'] } },
    { act: 'readScroll', w: 4 },
    { act: 'incense', w: 4, when: { places: ['shrine'] } },
    dry({ act: 'taichi', w: 3, when: DAWN }),
    { act: 'bow', w: 1 },
  ],
  child: [
    { act: 'umbrella', w: 4, when: { weather: WET } },
    { act: 'shelter', w: 6, when: { weather: WET } },
    { act: 'shiver', w: 6, when: { weather: ['snow'] } },
    dry({ act: 'tag', w: 8, when: { parts: ['day', 'dusk'] } }),
    dry({ act: 'shuttlecock', w: 8, when: { parts: ['day', 'dusk'] } }),
    { act: 'dice', w: 3 },
    { act: 'laugh', w: 3 },
    { act: 'wave', w: 1 },
    { act: 'scratchHead', w: 1 },
    { act: 'yawn', w: 4, when: { parts: ['night'] } },
    { act: 'skyLook', w: 3, when: { parts: ['night'] } },
    { act: 'eat', w: 2, when: MEAL },
  ],
  elder: [
    ...COMMON,
    dry({ act: 'taichi', w: 8, when: DAWN }),
    dry({ act: 'xiangqi', w: 8, when: { parts: ['day', 'dusk'] } }),
    { act: 'drinkTea', w: 5 },
    { act: 'sleepBench', w: 4, when: { periods: ['noon', 'afternoon'] } },
    { act: 'sigh', w: 2 },
    { act: 'storyteller', w: 2, when: { places: ['plaza', 'inn'] } },
  ],
  fisher: [
    ...COMMON,
    dry({ act: 'row', w: 6, when: { places: ['water'] } }),
    { act: 'carryBuckets', w: 3, when: { parts: ['dawn', 'day'] } },
    { act: 'drinkTea', w: 2, on: ['mend', 'fish'] },
    { act: 'wipeSweat', w: 2, when: HOT, on: ['fish', 'mend'] },
    { act: 'yawn', w: 1, on: ['fish'] },
  ],
  farmer: [
    ...COMMON,
    dry({ act: 'plantRice', w: 6, when: { places: ['field'], parts: ['dawn', 'day'] }, on: ['hoe', 'idle'] }),
    { act: 'wipeSweat', w: 4, when: HOT, on: ['hoe', 'idle'] },
    { act: 'stretch', w: 2, on: ['hoe'] },
    { act: 'carryBuckets', w: 3, when: { parts: ['dawn', 'day'] } },
    dry({ act: 'feedChickens', w: 3, when: { parts: ['dawn', 'day'] } }),
    { act: 'eat', w: 3, when: MEAL, on: ['hoe', 'idle', 'sit'] },
    dry({ act: 'chat', w: 2 }),
  ],
  smith: [
    ...COMMON,
    { act: 'hammer', w: 10, when: { parts: ['day', 'dusk'] } },
    { act: 'chopWood', w: 5, when: { parts: ['dawn', 'day'] } },
    { act: 'wipeSweat', w: 4 },
    { act: 'drinkTea', w: 2 },
  ],
  scholar: [
    ...COMMON,
    { act: 'readScroll', w: 8 },
    { act: 'calligraphy', w: 6, when: { notWeather: WET } },
    { act: 'skyLook', w: 3, when: { parts: ['night', 'dusk'] } },
    { act: 'fan', w: 4 },
    dry({ act: 'xiangqi', w: 4, when: { parts: ['day', 'dusk'] } }),
    { act: 'flute', w: 2, when: { parts: ['dusk', 'night'] } },
    { act: 'drinkTea', w: 3 },
  ],
  performer: [
    ...COMMON,
    { act: 'storyteller', w: 8, when: { notWeather: WET } },
    { act: 'guqin', w: 6 },
    { act: 'flute', w: 5 },
    { act: 'opera.sing', w: 3, when: { places: ['plaza', 'market'] } },
    { act: 'drum', w: 2, when: { places: ['plaza', 'market'], parts: ['day', 'dusk'] } },
    { act: 'drinkTea', w: 2 },
  ],
  disciple: [
    { act: 'umbrella', w: 5, when: { weather: WET } },
    dry({ act: 'taichi', w: 8, when: DAWN }),
    dry({ act: 'spar', w: 8, when: { parts: ['day', 'dusk'] } }),
    dry({ act: 'demo.form', w: 5, when: { parts: ['dawn', 'day'] } }),
    { act: 'meditate', w: 6 },
    { act: 'wipeSweat', w: 3, when: HOT },
    { act: 'readScroll', w: 2 },
    { act: 'stretch', w: 2 },
  ],
};

/** How each role meets the player and trouble. */
export interface RoleReact {
  /** A passer-by greeted. */
  readonly greet: NpcActId;
  /** A red name close by. */
  readonly pk: NpcActId;
  /** A fight close by. */
  readonly fight: NpcActId;
}

const CIVIL: RoleReact = { greet: 'bow', pk: 'cower', fight: 'flee' };
const MARTIAL: RoleReact = { greet: 'baoquan', pk: 'baoquan', fight: 'cheer' };
export const ROLE_REACT: Readonly<Record<NpcRole, RoleReact>> = {
  villager: CIVIL,
  vendor: CIVIL,
  innkeeper: CIVIL,
  farmer: CIVIL,
  fisher: CIVIL,
  scholar: CIVIL,
  performer: CIVIL,
  elder: { greet: 'bow', pk: 'sigh', fight: 'cower' },
  monk: { greet: 'bow', pk: 'bow', fight: 'cower' },
  child: { greet: 'wave', pk: 'cower', fight: 'flee' },
  guard: { greet: 'baoquan', pk: 'baoquan', fight: 'cheer' },
  smith: { ...CIVIL, greet: 'baoquan' },
  disciple: MARTIAL,
};

/** Townsfolk (data/folk.ts ids) and key NPCs (their `NPC_LINES` role) by role. */
export const FOLK_ROLES: Readonly<Record<string, NpcRole>> = {
  hai: 'fisher',
  yu: 'child',
  liu: 'innkeeper',
  niu: 'farmer',
  sun: 'farmer',
  zheng: 'guard',
  skyblade: 'disciple',
  moonwind: 'disciple',
  mist: 'disciple',
  mei: 'vendor',
  bao: 'child',
  lao: 'guard',
  elder: 'elder',
  merchant: 'vendor',
  guide: 'villager',
};

/** The kind of place round a landmark (folk stations and act people stand at landmarks). */
export const PLACE_OF: Readonly<Record<string, PlaceTag>> = {
  plaza: 'plaza',
  wake: 'water',
  pier: 'water',
  pierWest: 'water',
  pierEast: 'water',
  pierEnd: 'water',
  shore: 'water',
  pond: 'water',
  pavilion: 'garden',
  shrine: 'shrine',
  shrineTop: 'shrine',
  meiPost: 'shrine',
  inn: 'inn',
  shop: 'market',
  trainYard: 'yard',
  fieldsWest: 'field',
  fieldsEast: 'field',
  gate: 'street',
  guide: 'street',
  carriage: 'street',
  arena: 'arena',
  duel: 'arena',
};

/**
 * People who are there only for their acts (no schedule): the plaza's storyteller, the old men at
 * their game, the smith, the washerwomen, the children at play. `at` is a landmark (`plaza` the
 * map's plaza) shifted by `dx`, `dy` tiles, or `pos` a spot in map tiles (the region city's greybox,
 * placed only on a map that reaches it). `with` pairs two of them (index in this zone's list) for
 * the pair acts. `prefer` multiplies the weights of the role's acts.
 */
export interface ActPerson {
  readonly zone: ZoneId;
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  readonly role: NpcRole;
  readonly at?: string;
  readonly dx?: number;
  readonly dy?: number;
  readonly pos?: readonly [number, number];
  readonly place?: PlaceTag;
  readonly with?: number;
  readonly prefer?: Partial<Record<NpcActId, number>>;
  readonly child?: boolean;
}

/** ลานศิลาใหญ่, the region city's square (data/regions/yanyu.city.ts `CITY`: centre 1450, 1650; the stone in the middle). */
const SQ = (dx: number, dy: number): readonly [number, number] => [1450 + dx, 1650 + dy];

const BASE_PEOPLE: readonly ActPerson[] = [
  // ── หมู่บ้านหยานหยู: the plaza ──
  { zone: START, name: 'ซือหม่าเหลียง', title: 'นักเล่านิทาน', sprite: 'scholar', role: 'performer', at: 'plaza', dx: -5, dy: 3, prefer: { storyteller: 4, guqin: 0, flute: 0.3 } },
  { zone: START, name: 'ตาเฒ่าโจว', title: 'ผู้เฒ่าในหมู่บ้าน', sprite: 'fisherOld', role: 'elder', at: 'plaza', dx: 4, dy: -3.4, with: 2 },
  { zone: START, name: 'ตาเฒ่าหวัง', title: 'ผู้เฒ่าในหมู่บ้าน', sprite: 'stationMaster', role: 'elder', at: 'plaza', dx: 5.5, dy: -3.4, with: 1, prefer: { storyteller: 0 } },
  { zone: START, name: 'แม่ค้าเฉิน', title: 'แม่ค้าซาลาเปา', sprite: 'teaSeller', role: 'vendor', at: 'plaza', dx: -3, dy: -4 },
  { zone: START, name: 'เสี่ยวหลง', title: 'เด็กในหมู่บ้าน', sprite: 'fisherKid', role: 'child', at: 'plaza', dx: 1.5, dy: 4, with: 5, child: true },
  { zone: START, name: 'เสี่ยวฮวา', title: 'เด็กในหมู่บ้าน', sprite: 'fisherKid', role: 'child', at: 'plaza', dx: 3, dy: 4.5, with: 4, child: true },
  { zone: START, name: 'ช่างเหล็กหลี่', title: 'ช่างตีเหล็ก', sprite: 'farmer', role: 'smith', at: 'trainYard', dx: 4, dy: -3 },
  { zone: START, name: 'หลวงจีนจิ้งซิน', title: 'พระเฝ้าศาล', sprite: 'monk', role: 'monk', at: 'shrine', dx: -2, dy: 0.5, place: 'shrine' },
  { zone: START, name: 'ศิษย์อาเจี๋ย', title: 'ศิษย์ฝึกวิชา', sprite: 'guardOfficer', role: 'disciple', at: 'trainYard', dx: -1, dy: 1, with: 9 },
  { zone: START, name: 'ศิษย์อาเฟิง', title: 'ศิษย์ฝึกวิชา', sprite: 'taoist', role: 'disciple', at: 'trainYard', dx: 0.6, dy: 1, with: 8 },
  { zone: START, name: 'ป้าหลัว', title: 'แม่บ้าน', sprite: 'farmerAunt', role: 'villager', at: 'pond', dx: 1.5, dy: 1.5, place: 'water', prefer: { washClothes: 3 } },
  { zone: START, name: 'บัณฑิตเจียง', title: 'บัณฑิตสอบตก', sprite: 'scholar', role: 'scholar', at: 'pavilion', dx: -1.5, dy: 0 },
  { zone: START, name: 'แม่นางอวิ๋น', title: 'นักดนตรี', sprite: 'blissLady', role: 'performer', at: 'pavilion', dx: -3, dy: 1, prefer: { storyteller: 0, guqin: 2 } },
  { zone: START, name: 'อาซาน', title: 'ขี้เมาประจำโรงเตี๊ยม', sprite: 'bandit', role: 'villager', at: 'inn', dx: 2.5, dy: 2, prefer: { drunk: 3, dice: 2 } },
  { zone: START, name: 'ตาแป๊ะเฉา', title: 'คนแจวเรือ', sprite: 'fisherOld', role: 'fisher', at: 'pierWest', dx: 0, dy: 1, place: 'water', prefer: { row: 3 } },
  { zone: START, name: 'พลทหารหม่า', title: 'ยามลาดตระเวน', sprite: 'watchman', role: 'guard', at: 'plaza', dx: 6, dy: 2 },
  { zone: START, name: 'อาเหมียว', title: 'ชาวนา', sprite: 'farmerAunt', role: 'farmer', at: 'fieldsWest', dx: 1, dy: 1, place: 'field', prefer: { plantRice: 3 } },
  // ── ลานศิลาใหญ่ (the region city's square, greybox) ──
  { zone: 'yanyu', name: 'นักเล่านิทานเฒ่า', title: 'นักเล่านิทาน', sprite: 'scholar', role: 'performer', pos: SQ(-30, -40), place: 'plaza', prefer: { storyteller: 4 } },
  { zone: 'yanyu', name: 'พ่อค้าหวาน', title: 'พ่อค้าขนม', sprite: 'merchant', role: 'vendor', pos: SQ(36, -36), place: 'market' },
  { zone: 'yanyu', name: 'แม่ค้าชา', title: 'แม่ค้าน้ำชา', sprite: 'teaSeller', role: 'vendor', pos: SQ(40, -30), place: 'market' },
  { zone: 'yanyu', name: 'ลูกค้าต่อราคา', title: 'ชาวเมือง', sprite: 'farmer', role: 'villager', pos: SQ(38, -33), place: 'market', prefer: { haggle: 6 } },
  { zone: 'yanyu', name: 'นักพรตเดินทาง', title: 'นักพรต', sprite: 'taoist', role: 'disciple', pos: SQ(-40, 30), with: 22, place: 'plaza' },
  { zone: 'yanyu', name: 'ศิษย์สำนักเหวิน', title: 'ศิษย์สำนัก', sprite: 'guardOfficer', role: 'disciple', pos: SQ(-38.8, 30), with: 21, place: 'plaza' },
  { zone: 'yanyu', name: 'ตาเฒ่าเซี่ย', title: 'ชาวเมือง', sprite: 'fisherOld', role: 'elder', pos: SQ(25, 35), with: 24, place: 'plaza' },
  { zone: 'yanyu', name: 'ตาเฒ่าเผิง', title: 'ชาวเมือง', sprite: 'stationMaster', role: 'elder', pos: SQ(26.5, 35), with: 23, place: 'plaza' },
  { zone: 'yanyu', name: 'เด็กเมืองอาลี่', title: 'เด็กในเมือง', sprite: 'fisherKid', role: 'child', pos: SQ(-10, 40), with: 26, child: true, place: 'plaza' },
  { zone: 'yanyu', name: 'เด็กเมืองอาเป่า', title: 'เด็กในเมือง', sprite: 'fisherKid', role: 'child', pos: SQ(-8, 41), with: 25, child: true, place: 'plaza' },
  { zone: 'yanyu', name: 'ยามประตูเมือง', title: 'ยามเมือง', sprite: 'watchman', role: 'guard', pos: SQ(0, -60), place: 'street' },
  { zone: 'yanyu', name: 'หลวงจีนบิณฑบาต', title: 'พระธุดงค์', sprite: 'monk', role: 'monk', pos: SQ(-20, -24), place: 'plaza' },
];

/** The mirror of the region (data/regions/jiuzhou.ts, `flipSketch`): y -> this - y. */
const REGION_SIDE = 3456;

/**
 * The hub city's people again on แคว้นเก้ากระบี่, where the same city lies mirrored (y -> 3456 - y), keyed to the new Yanyu's
 * zone. Their pairs (`with`) point at the copies.
 */
const CITY_COPY: readonly ActPerson[] = (() => {
  const idx = BASE_PEOPLE.map((p, k) => (p.pos ? k : -1)).filter((k) => k >= 0);
  return idx.map((k) => {
    const p = BASE_PEOPLE[k]!;
    const w = p.with !== undefined ? idx.indexOf(p.with) : -1;
    return {
      ...p,
      zone: START,
      pos: [p.pos![0], REGION_SIDE - p.pos![1]] as const,
      ...(w >= 0 ? { with: BASE_PEOPLE.length + w } : {}),
    };
  });
})();

export const ACT_PEOPLE: readonly ActPerson[] = [...BASE_PEOPLE, ...CITY_COPY];
